 
import React, { useEffect, useMemo, useState } from 'react';
import { DEFAULT_COVER } from '@/constants/media';
import { REMOTE_FALLBACK_COVER_URL } from '@/constants/images';
import safeToast from '@/utils/safeToast';
import { X, Save, RotateCcw, Palette, User, Image, Globe, Eye, Crown, Lock, MailCheck } from 'lucide-react';
import type { User as UserType } from '@/types';
import { listTemplatesMeta, type TemplateMeta } from '@/api/templates';
import { PreviewCard as MetaPreviewCard } from '@/components/templates/MetaPreviewCard';
import PhoneBezel from '@/components/templates/common/PhoneBezel';
import { PreviewCard as StaticPreview, resolveVariant } from '@/components/templates/PreviewCard';
import { ECardTemplate1 } from '@/components/ECardTemplate1';
import { ECardTemplate2 } from '@/components/ECardTemplate2';
import { ECardTemplate3 } from '@/components/ECardTemplate3';
import { ECardTemplate4 } from '@/components/ECardTemplate4';
import { ECardTemplate5 } from '@/components/ECardTemplate5';
import { uploadImage } from '@/api/uploads';
import { getCurrentUser } from '@/api/auth';
import { isSuperAdmin, isBasicAdmin } from '@/utils/permissions';
import { getCardByUsername, updateCardContactEmail, updateCardDisplayEmail } from '@/api/cards';

/**
 * CardCustomizationPage
 * Full-feature panel (Template, Colors, Profile, Social, Images, Subscription, Admin Password)
 * Includes robust dynamic-template handling:
 *  - Prefer persisted templateMetaId for previews and selection
 *  - Clean save payload so templateMetaId and templateId don't fight each other
 *  - Avoid flashing back to classic variants while meta loads (reads from localStorage cache)
 */

interface CardCustomizationPageProps {
  user: UserType;
  ads: any[];
  onClose: () => void;
  onSave: (userId: string, updates: Partial<UserType>) => void;
}

type TabId = 'template' | 'colors' | 'profile' | 'social' | 'images' | 'subscription' | 'password';

type AvailableTemplate = {
  key: string; // 'builtin/<n>' | 'srv/<id>'
  variantId: '1' | '2' | '3' | '4' | '5';
  name: string;
  description?: string;
  category?: 'business' | 'artist';
  previewUrl?: string;
  source: 'builtin' | 'server';
};

export const CardCustomizationPage: React.FC<CardCustomizationPageProps> = ({
  user,
  ads,
  onClose,
  onSave,
}) => {
  // ---------- Defaults / State ----------
  const defaultColors = {
    primary: '#3b82f6',
    secondary: '#1e40af',
    accent: '#f59e0b',
    background: '#f8fafc',
    text: '#1f2937',
    stripLine: '#3b82f6',
    saveButton: '#3b82f6',
    companyBadge: '#8B2635',
  };

  const [editedUser, setEditedUser] = useState<UserType>({
    ...user,
    colors: { ...defaultColors, ...(user.colors || {}) },
  });

  const [activeTab, setActiveTab] = useState<TabId>('template');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [permLoading, setPermLoading] = useState(true);
  const canSetPassword = useMemo(
    () => isSuperAdmin(currentUser as any) || isBasicAdmin(currentUser as any),
    [currentUser]
  );

  // Admin password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);

  // Contact email (Card.fields.email) state
  const [contactEmail, setContactEmail] = useState<string>('');
  const [syncAccount, setSyncAccount] = useState<boolean>(true);
  const [savingContact, setSavingContact] = useState<boolean>(false);

  // Template data
  const [availableTemplates, setAvailableTemplates] = useState<AvailableTemplate[]>([]);
  const [templatesMetaById, setTemplatesMetaById] = useState<Record<string, TemplateMeta>>({});
  const [tplLoading, setTplLoading] = useState(false);
  const [templateCategory, setTemplateCategory] = useState<'business' | 'artist'>(() => {
    try {
      const saved = localStorage.getItem('ccp_template_category');
      if (saved === 'business' || saved === 'artist') return saved;
    } catch {}
    return 'business';
  });

  // Track current selection in the picker UI
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(() => {
    const metaId = (user as any)?.templateMetaId;
    return metaId ? `srv/${metaId}` : null;
  });
  const [selectedServerTemplateId, setSelectedServerTemplateId] = useState<string | null>(
    (user as any)?.templateMetaId || null
  );

  // Preview bezel width; allow height to grow with content for auto-resize
  const FIXED_PHONE_WIDTH = 360;

  // ---------- Effects ----------
  useEffect(() => {
    let alive = true;
    getCurrentUser()
      .then((u) => {
        if (!alive) return;
        setCurrentUser(u as any);
      })
      .catch(() => {})
      .finally(() => {
        if (!alive) return;
        setPermLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('ccp_template_category', templateCategory);
    } catch {}
  }, [templateCategory]);

  useEffect(() => {
    buildTemplatesList();
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'templates_updated_ts') buildTemplatesList();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
     
  }, []);

  // Load card contact email on mount/user change
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const un = String((user as any)?.username || '').trim().toLowerCase();
        if (!un) return;
        const card = await getCardByUsername(un).catch(() => null);
        const fe = (card && card.fields && typeof card.fields.email === 'string') ? String(card.fields.email) : '';
        if (!alive) return;
        setContactEmail(fe || (user.email || ''));
      } catch {
        if (!alive) return;
        setContactEmail(user.email || '');
      }
    })();
    return () => { alive = false; };
  }, [user]);

  // When the admin switches to another user in the parent table
  useEffect(() => {
    const metaId = (user as any)?.templateMetaId;
    if (metaId) {
      setSelectedTemplateKey(`srv/${metaId}`);
      setSelectedServerTemplateId(metaId);
    } else {
      setSelectedTemplateKey(null);
      setSelectedServerTemplateId(null);
    }
    setEditedUser({
      ...user,
      colors: { ...defaultColors, ...(user.colors || {}) },
    });
  }, [user]);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      try {
        if (avatarPreview) URL.revokeObjectURL(avatarPreview);
      } catch {}
      try {
        if (coverPreview) URL.revokeObjectURL(coverPreview);
      } catch {}
    };
  }, [avatarPreview, coverPreview]);

  // ---------- Helpers ----------
  const pwdChecks = (pwd: string) => {
    const lengthOk = pwd.length >= 8;
    const hasNumber = /\d/.test(pwd);
    const hasSymbol = /[^A-Za-z0-9]/.test(pwd);
    const hasUpper = /[A-Z]/.test(pwd);
    const hasLower = /[a-z]/.test(pwd);
    const score = [lengthOk, hasNumber, hasSymbol, hasUpper && hasLower].filter(Boolean).length;
    const label = score >= 4 ? 'Strong' : score === 3 ? 'Good' : score === 2 ? 'Fair' : 'Weak';
    const color =
      score >= 4 ? 'bg-green-500' : score === 3 ? 'bg-yellow-500' : score === 2 ? 'bg-orange-500' : 'bg-red-500';
    return { lengthOk, hasNumber, hasSymbol, hasUpper, hasLower, score, label, color };
  };

  const mapToTemplateId = (t: { componentKey?: string; name?: string }): '1' | '2' | '3' | '4' | '5' => {
    const rawKey = String(t.componentKey || '').trim().toLowerCase();
    if (['1', '2', '3', '4', '5'].includes(rawKey)) return rawKey as any;
    if (/soft[-_\s]?blossom/.test(rawKey) || String(t.name || '').toLowerCase().includes('soft blossom')) return '2';
    if (/professional-classic/.test(rawKey)) return '1';
    if (/elegant-looking/.test(rawKey)) return '2';
    if (/modern-clean/.test(rawKey)) return '3';
    if (/golden-view/.test(rawKey)) return '4';
    if (/classic-modular/.test(rawKey)) return '5';
    return '1';
  };

  async function buildTemplatesList() {
    setTplLoading(true);
    try {
      const admin = isSuperAdmin(currentUser as any) || isBasicAdmin(currentUser as any);
      const items = await listTemplatesMeta(admin ? undefined : { approvedOnly: true, activeOnly: true });

      const fromServer: AvailableTemplate[] = (items || []).map((it) => ({
        key: `srv/${it.id}`,
        variantId: mapToTemplateId({ componentKey: it.componentKey, name: it.name }),
        name: it.name,
        description: it.description,
        category: String((it as any).category || 'business').toLowerCase() as 'business' | 'artist',
        previewUrl: (it as any).previewUrl || (it as any).coverUrl,
        source: 'server',
      }));

      const builtins: AvailableTemplate[] = [
        { key: 'builtin/1', variantId: '1', name: 'Professional Classic', category: 'business', source: 'builtin' },
        { key: 'builtin/2', variantId: '2', name: 'Elegant Looking', category: 'business', source: 'builtin' },
        { key: 'builtin/3', variantId: '3', name: 'Modern and Clean', category: 'business', source: 'builtin' },
        { key: 'builtin/4', variantId: '4', name: 'Golden View', category: 'business', source: 'builtin' },
        { key: 'builtin/5', variantId: '5', name: 'Classic Modular', category: 'business', source: 'builtin' },
      ];

      const m: Record<string, TemplateMeta> = {};
      (items || []).forEach((it) => {
        if (it?.id) m[it.id] = it;
      });
      setTemplatesMetaById(m);
      try {
        localStorage.setItem('templates_cache_meta', JSON.stringify(m));
      } catch {}

  // Prefer builtin classics over server variants by ordering builtins first;
  // the display list later de-duplicates by name so builtins win for classics.
  setAvailableTemplates([...builtins, ...fromServer]);
    } catch (e) {
      setAvailableTemplates([]);
      setTemplatesMetaById({});
    } finally {
      setTplLoading(false);
    }
  }

  const mergedColors = useMemo(
    () => ({ ...defaultColors, ...((editedUser as any).colors || {}) }),
    [editedUser]
  );

  // ---------- Save ----------
  const handleSave = async () => {
    if (isUploading) {
      safeToast('info', 'Please wait for image upload to finish');
      return;
    }

    const ensureHttp = (url: string) => {
      const t = String(url || '').trim();
      if (!t) return t;
      if (/^https?:\/\//i.test(t)) return t;
      return `https://${t}`;
    };

    const aliasPlatform = (k: string, v: string) => {
      const key = String(k || '').toLowerCase();
      if (key === 'x' || key.includes('twitter')) return 'twitter';
      if (key.includes('linkedin')) return 'linkedin';
      if (key.includes('instagram')) return 'instagram';
      if (key.includes('facebook')) return 'facebook';
      if (key.includes('tiktok')) return 'tiktok';
      return key;
    };

    const cleanRecord = (obj: any) => {
      if (!obj || typeof obj !== 'object') return obj;
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'string' && v.trim() !== '') {
          const nk = aliasPlatform(k, v);
          out[nk] = ensureHttp(v);
        }
      }
      return out;
    };

    const blankToNull = (v: any) => (typeof v === 'string' && v.trim() === '' ? null : v);

    // Compute social/streaming normalized
    const socialClean = cleanRecord((editedUser as any).socialLinks || {});
    const streamingClean = cleanRecord((editedUser as any).streamingLinks || {});

    // Store the display email separately - will be saved to Card.fields.email only
    const displayEmail = blankToNull((editedUser as any).email);

    const payload: any = {
      ...editedUser,
      name: blankToNull((editedUser as any).name),
      displayName: blankToNull((editedUser as any).displayName),
      company: blankToNull((editedUser as any).company),
      position: blankToNull((editedUser as any).position),
      // DO NOT include email in payload - it will be updated separately via Card.fields.email
      location: blankToNull((editedUser as any).location),
      bio: blankToNull((editedUser as any).bio),
      website: (() => {
        const raw = (editedUser as any).website;
        if (typeof raw === 'string' && raw.trim() === '') return null;
        return ensureHttp(raw || '');
      })(),
      phone: (() => {
        const p = (editedUser as any).phone;
        return typeof p === 'string' && p.trim() === '' ? null : p;
      })(),
      socialLinks: socialClean && Object.keys(socialClean).length === 0 ? null : socialClean,
      streamingLinks: streamingClean && Object.keys(streamingClean).length === 0 ? null : streamingClean,
    };

    // Remove email from payload to prevent updating account email
    delete payload.email;

    // Template persistence (exclusive)
    const effectiveMetaId =
      (editedUser as any)?.templateMetaId ||
      selectedServerTemplateId ||
      (selectedTemplateKey?.startsWith('srv/') ? selectedTemplateKey.replace(/^srv\//, '') : null) ||
      null;

    if (effectiveMetaId) {
      payload.templateMetaId = effectiveMetaId;
      delete payload.templateId; // don't let static override
    } else {
      delete payload.templateMetaId;
      payload.templateId = (editedUser as any)?.templateId || '1';
    }

    // Do not persist blob/data URLs
    if (typeof payload.avatar === 'string' && /^(blob:|data:)/i.test(payload.avatar)) delete payload.avatar;
    if (typeof payload.coverImage === 'string' && /^(blob:|data:)/i.test(payload.coverImage)) delete payload.coverImage;

    onSave(user.id, payload);

    // Update Card display email separately (does NOT change contact/login email)
    if (displayEmail) {
      const username = String((user as any)?.username || '').trim();
      if (username && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(displayEmail)) {
        try {
          await updateCardDisplayEmail(username, displayEmail);
        } catch (e) {
          console.warn('Failed to update card display email:', e);
          // Don't block the save if this fails
        }
      }
    }

    // Broadcast and UX
    try {
      const stamp = Date.now();
      const evt = {
        id: user.id,
        username: (payload as any)?.username || (user as any)?.username,
        handle: (payload as any)?.handle || (user as any)?.handle,
        ts: stamp,
      };
      localStorage.setItem('public_card_refresh', JSON.stringify(evt));
      setTimeout(() => {
        try {
          localStorage.removeItem('public_card_refresh');
        } catch {}
      }, 60);
    } catch {}

    safeToast('success', 'User card customization saved successfully');
    onClose();
  };

  const handleReset = () => {
    setEditedUser((prev: any) => ({
      ...prev,
      colors: { ...defaultColors },
      templateId: '1',
      templateMetaId: undefined,
    }));
    setSelectedTemplateKey('builtin/1');
    setSelectedServerTemplateId(null);
  };

  // Save Card contact email handler
  const handleSaveContactEmail = async () => {
    const email = String(contactEmail || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      safeToast('error', 'Enter a valid contact email');
      return;
    }
    const username = String((user as any)?.username || '').trim();
    if (!username) {
      safeToast('error', 'Missing username');
      return;
    }
    try {
      setSavingContact(true);
      const res = await updateCardContactEmail(username, email, { setAsLoginEmail: syncAccount });
      
      // Contact email is always updated (for notifications, leads, security alerts)
      // Login email is only updated if checkbox is checked
      if (res?.loginEmailUpdated) {
        safeToast('success', 'Contact email updated and set as login email');
      } else {
        safeToast('success', 'Contact email updated (login email unchanged)');
      }
    } catch (e: any) {
      const msg = e?.data?.error || e?.message || 'Failed to update contact email';
      safeToast('error', msg);
    } finally {
      setSavingContact(false);
    }
  };

  // ---------- Image Upload ----------
  const handleImageUpload = async (type: 'avatar' | 'cover', file?: File) => {
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    if (type === 'avatar') setAvatarPreview(localUrl);
    else setCoverPreview(localUrl);

    try {
      setIsUploading(true);
      const uploadedUrl = await uploadImage(file);
      if (!uploadedUrl) throw new Error('Empty upload URL returned by server');
      setEditedUser((prev: any) => ({
        ...prev,
        ...(type === 'avatar' ? { avatar: uploadedUrl } : { coverImage: uploadedUrl }),
      }));
      safeToast('success', `${type === 'avatar' ? 'Avatar' : 'Cover image'} uploaded`);
    } catch (e: any) {
      console.error('[CardCustomizationPage] upload failed', e);
      safeToast('error', e?.message || 'Image upload failed');
    } finally {
      setIsUploading(false);
      try {
        URL.revokeObjectURL(localUrl);
      } catch {}
      if (type === 'avatar') setAvatarPreview(null);
      else setCoverPreview(null);
    }
  };

  // ---------- Admin: Set Password ----------
  const handleAdminSetPassword = async () => {
    try {
      if (!canSetPassword) {
        safeToast('error', 'You do not have permission to set passwords');
        return;
      }
      const token = typeof window !== 'undefined' ? localStorage.getItem('dev_admin_token') : null;
      if (!token) {
        safeToast('error', 'Admin authentication required');
        return;
      }
      const pwd = String(newPassword || '');
      const confirm = String(confirmPassword || '');
      const checks = pwdChecks(pwd);
      if (!checks.lengthOk) {
        safeToast('error', 'Password must be at least 8 characters');
        return;
      }
      // Validation differs by context: Profile tab is a user convenience flow,
      // Admin tab enforces stronger policy.
      const inProfileTab = activeTab === 'profile';
      if (inProfileTab) {
        if (!(checks.hasNumber || checks.hasSymbol)) {
          safeToast('error', 'Password should include a number or a symbol');
          return;
        }
      } else {
        if (!checks.hasNumber || !checks.hasSymbol) {
          safeToast('error', 'Password must include at least one number and one symbol');
          return;
        }
      }
      if (pwd !== confirm) {
        safeToast('error', 'Passwords do not match');
        return;
      }
      const idOrUsername = String((user as any).id || (user as any)._id || (user as any).username || '').trim();
      if (!idOrUsername) {
        safeToast('error', 'Missing user identifier');
        return;
      }
      setIsSettingPassword(true);
      const res = await fetch(`/api/admin/users/${encodeURIComponent(idOrUsername)}/password`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ password: pwd }),
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => 'Failed to set password');
        throw new Error(msg || 'Failed to set password');
      }
      safeToast('success', 'Password updated successfully');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e: any) {
      console.error('[CardCustomizationPage] set password failed', e);
      safeToast('error', e?.message || 'Unable to set password');
    } finally {
      setIsSettingPassword(false);
    }
  };

  // ---------- Rendering Helpers ----------
  const renderTemplatePreview = () => {
    // Prefer dynamic meta id only when truly present; otherwise render classic immediately
    const savedMetaId =
      ((editedUser as any)?.templateMetaId ? String((editedUser as any)?.templateMetaId) : '') ||
      (selectedServerTemplateId ? String(selectedServerTemplateId) : '') ||
      (selectedTemplateKey?.startsWith('srv/') ? selectedTemplateKey.replace(/^srv\//, '') : '') ||
      '';

    // First, if a dynamic server template is selected (e.g., Soft Blossom), render it in live preview
    // even if a legacy classic templateId is still present in the record.
    let meta = savedMetaId ? templatesMetaById?.[savedMetaId] : undefined;
    if (!meta && savedMetaId) {
      try {
        const raw = localStorage.getItem('templates_cache_meta');
        if (raw) {
          const cache = JSON.parse(raw) || {};
          meta = cache[savedMetaId] || undefined;
        }
      } catch {}
    }

    const isDynamic = !!meta && ((meta as any).renderer?.startsWith('dynamic-') || !!(meta as any).spec);
    if (isDynamic && meta) {
      const cover = (editedUser as any).coverImage || (editedUser as any).coverUrl || DEFAULT_COVER;
      return (
        <MetaPreviewCard
          template={meta}
          user={{
            ...editedUser,
            avatarUrl: (editedUser as any).avatar || (editedUser as any).avatarUrl,
            coverUrl: cover,
            coverImage: cover,
          }}
            // Render content only; outer panel adds a unified PhoneBezel and handles scale-to-fit
            mode="full"
          frame="none"
          // Allow inner scroll sections (if any) instead of clipping
          allowInnerScroll={true}
          ads={Array.isArray(ads) && ads.length === 0 ? undefined : ads}
          isOwnerView={true}
        />
      );
    }

    // If no dynamic meta is selected/available, fall back to rendering classics when explicitly set
    const tplId = String((editedUser as any)?.templateId || '');
    const isClassic = ['1','2','3','4','5'].includes(tplId);
    if (isClassic) {
      const templateProps = {
        user: editedUser as any,
        ads: Array.isArray(ads) ? ads : [],
        isOwnerView: true,
        onAnalyticsUpdate: () => {},
        onLeadSubmit: () => {},
        leads: [] as any[],
      } as const;
      // Render classic templates without an internal phone frame; the outer panel adds a unified PhoneBezel
      switch (tplId) {
        case '2':
          return <ECardTemplate2 {...templateProps} />;
        case '3':
          return <ECardTemplate3 {...templateProps} />;
        case '4':
          return <ECardTemplate4 {...templateProps} />;
        case '5':
          return <ECardTemplate5 {...templateProps} />;
        default:
          return <ECardTemplate1 {...templateProps} />;
      }
    }

    // If a dynamic meta id appears to be chosen but not loaded yet, only show the loader
    // when no classic templateId is set. If a classic id exists, render classic immediately.
    if (savedMetaId && !meta && !isClassic) {
      return (
        <div className="w-full flex items-center justify-center py-10">
          <div className="rounded-xl border bg-white shadow-sm px-4 py-3 text-sm text-gray-600">
            Generating live preview…
          </div>
        </div>
      );
    }

    // Static preview fallback: render real classic components when templateId matches 1..5;
    // otherwise keep the static resolver as a safety net
    const templateId = String((editedUser as any)?.templateId || '');
    const templateProps = {
      user: editedUser as any,
      ads: Array.isArray(ads) ? ads : [],
      isOwnerView: true,
      onAnalyticsUpdate: () => {},
      onLeadSubmit: () => {},
      leads: [] as any[],
    } as const;
    if (['1','2','3','4','5'].includes(templateId)) {
      switch (templateId) {
        case '2':
          return <ECardTemplate2 {...templateProps} />;
        case '3':
          return <ECardTemplate3 {...templateProps} />;
        case '4':
          return <ECardTemplate4 {...templateProps} />;
        case '5':
          return <ECardTemplate5 {...templateProps} />;
        default:
          return <ECardTemplate1 {...templateProps} />;
      }
    }
    const v = resolveVariant(
      (editedUser as any)?.templateKey || (editedUser as any)?.templateId || 'professional-classic'
    );
    return <StaticPreview variant={v} user={editedUser} mode="full" />;
  };

  // ---------- UI ----------
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-7xl h-[90vh] flex overflow-hidden">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Customize Card</h2>
              <p className="text-blue-100">Editing {(user as any)?.name || (user as any)?.username}</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleReset}
                className="flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </button>
              <button
                onClick={handleSave}
                className="flex items-center px-6 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </button>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex w-full pt-24">
          {/* Left Panel */}
          <div className="w-1/2 p-6 overflow-y-auto bg-gray-50">
            {/* Tabs */}
            <div className="flex space-x-2 mb-6 bg-white rounded-lg p-1">
              {[
                { id: 'template', label: 'Template', icon: Palette },
                { id: 'colors', label: 'Colors', icon: Palette },
                { id: 'profile', label: 'Profile', icon: User },
                { id: 'social', label: 'Social', icon: Globe },
                { id: 'images', label: 'Images', icon: Image },
                { id: 'subscription', label: 'Subscription', icon: Crown },
                { id: 'password', label: 'Password', icon: Lock },
              ].map((tab) => {
                const IconComponent = tab.icon as any;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabId)}
                    className={`flex-1 flex items-center justify-center py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      activeTab === (tab.id as TabId)
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <IconComponent className="w-4 h-4 mr-1" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* TEMPLATE TAB */}
            {activeTab === 'template' && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">Choose Template</h3>
                  <div className="flex items-center bg-white rounded-md border p-1 shadow-sm shrink-0">
                    <button
                      type="button"
                      onClick={() => setTemplateCategory('business')}
                      className={`px-3 py-1.5 text-sm rounded ${
                        templateCategory === 'business' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Business
                    </button>
                    <button
                      type="button"
                      onClick={() => setTemplateCategory('artist')}
                      className={`px-3 py-1.5 text-sm rounded ${
                        templateCategory === 'artist' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Artist
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          localStorage.removeItem('templates_cache_meta');
                        } catch {}
                        await buildTemplatesList();
                        safeToast('info', 'Templates refreshed');
                      }}
                      className="px-3 py-1.5 text-sm rounded border bg-white hover:bg-gray-50"
                    >
                      Refresh
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {(() => {
                    const base = (availableTemplates || []).filter(
                      (t) => (t.category || 'business') === templateCategory
                    );
                    const seen = new Set<string>();
                    const display = base.filter((t) => {
                      const k = (t.name || '').trim().toLowerCase();
                      if (!k) return true;
                      if (seen.has(k)) return false;
                      seen.add(k);
                      return true;
                    });

                    if (display.length === 0 && tplLoading) {
                      return (
                        <div className="p-4 rounded-lg border bg-white text-sm text-gray-600">
                          Loading templates…
                        </div>
                      );
                    }

                    const handleSelect = (template: AvailableTemplate) => {
                      setSelectedTemplateKey(template.key);
                      if (template.source === 'server') {
                        const id = template.key.replace(/^srv\//, '');
                        setSelectedServerTemplateId(id);
                        // Lock dynamic selection in editedUser; clear static
                        setEditedUser((prev: any) => ({ ...prev, templateMetaId: id, templateId: undefined }));
                      } else {
                        // Classic selection
                        setSelectedServerTemplateId(null);
                        setEditedUser((prev: any) => ({
                          ...prev,
                          templateId: template.variantId,
                          templateMetaId: undefined,
                        }));
                      }
                    };

                    const dynId = (editedUser as any)?.templateMetaId || selectedServerTemplateId || null;

                    return display.map((template) => {
                      const isSelected = dynId
                        ? template.key === `srv/${dynId}`
                        : selectedTemplateKey
                        ? selectedTemplateKey === template.key
                        : (editedUser as any)?.templateId === template.variantId;

                      return (
                        <div
                          key={template.key}
                          onClick={() => handleSelect(template)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-900">{template.name}</h4>
                            {tplLoading && <span className="text-xs text-gray-500">Refreshing…</span>}
                          </div>
                          {template.description && (
                            <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* COLORS TAB */}
            {activeTab === 'colors' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Color Scheme</h3>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(mergedColors).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 capitalize">
                        {key === 'companyBadge'
                          ? 'Company Badge'
                          : key === 'saveButton'
                          ? 'Save Button'
                          : key.replace(/([A-Z])/g, ' $1').trim()}
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="color"
                          value={String(value || '')}
                          onChange={(e) => setEditedUser((p: any) => ({ ...p, colors: { ...p.colors, [key]: e.target.value } }))}
                          className="w-12 h-10 rounded border cursor-pointer"
                        />
                        <input
                          type="text"
                          value={String(value || '')}
                          onChange={(e) => setEditedUser((p: any) => ({ ...p, colors: { ...p.colors, [key]: e.target.value } }))}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PROFILE TAB */}
            {activeTab === 'profile' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Profile Information</h3>
                <div className="grid grid-cols-1 gap-4">
                  {['name', 'position', 'company', 'phone', 'email', 'website'].map((field) => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                        {field}
                      </label>
                      <input
                        type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : field === 'website' ? 'url' : 'text'}
                        value={(editedUser as any)[field] || ''}
                        onChange={(e) => setEditedUser((p: any) => ({ ...p, [field]: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    <textarea
                      value={(editedUser as any).bio || ''}
                      onChange={(e) => setEditedUser((p: any) => ({ ...p, bio: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
                    />
                  </div>
                </div>

                {/* Contact Email for Card (distinct from account email) */}
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <MailCheck className="w-4 h-4 text-blue-600" />
                    <h4 className="font-semibold text-gray-900 text-sm">Card Contact Email</h4>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      className="rounded-lg border border-gray-300 px-3 py-2 col-span-2"
                      placeholder="contact@example.com"
                    />
                    <button
                      type="button"
                      onClick={handleSaveContactEmail}
                      disabled={savingContact}
                      className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 disabled:opacity-60"
                    >
                      {savingContact ? 'Saving…' : 'Save Contact Email'}
                    </button>
                  </div>
                  <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" className="rounded" checked={syncAccount} onChange={(e)=>setSyncAccount(e.target.checked)} />
                    Also use this email for login
                  </label>
                  <p className="mt-2 text-xs text-gray-500">
                    Contact email receives notifications, leads, and security alerts. 
                    Check this box to also use this email for account login. Uncheck to keep your original signup email for login.
                  </p>
                </div>

                {/* Admin quick password change (restored in Profile) */}
                {canSetPassword && (
                  <section className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-100">
                    <div className="flex items-center gap-3 mb-3">
                      <Lock className="w-5 h-5 text-indigo-600" />
                      <h4 className="font-semibold text-gray-900">Change password</h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="New password"
                      />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="Confirm new password"
                      />
                      <div className="flex items-center gap-3">
                        <label className="flex items-center text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="mr-2"
                            checked={showPassword}
                            onChange={(e) => setShowPassword(e.target.checked)}
                          />
                          Show
                        </label>
                        <button
                          type="button"
                          onClick={handleAdminSetPassword}
                          className="ml-auto px-4 py-2 rounded-lg bg-indigo-600 text-white shadow hover:bg-indigo-700 disabled:opacity-60"
                          disabled={(() => {
                            const c = pwdChecks(newPassword);
                            return (
                              isSettingPassword ||
                              !c.lengthOk ||
                              // In Profile tab, require number OR symbol; elsewhere require both
                              (activeTab === 'profile' ? !(c.hasNumber || c.hasSymbol) : (!c.hasNumber || !c.hasSymbol)) ||
                              newPassword !== confirmPassword
                            );
                          })()}
                        >
                          {isSettingPassword ? 'Updating…' : 'Update password'}
                        </button>
                      </div>
                    </div>
                  </section>
                )}
              </div>
            )}

            {/* SOCIAL TAB */}
            {activeTab === 'social' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Social & Streaming Links</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Social</h4>
                    {['linkedin', 'twitter', 'instagram', 'facebook', 'tiktok'].map((platform) => (
                      <div key={platform} className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">{platform}</label>
                        <input
                          type="url"
                          value={((editedUser as any).socialLinks || {})[platform] || ''}
                          onChange={(e) =>
                            setEditedUser((p: any) => ({
                              ...p,
                              socialLinks: { ...(p.socialLinks || {}), [platform]: e.target.value },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          placeholder={`https://${platform}.com/yourprofile`}
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Streaming</h4>
                    {['spotify', 'youtube', 'soundcloud', 'boomplay', 'deezer', 'tidal', 'audiomack'].map(
                      (platform) => (
                        <div key={platform} className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                            {platform}
                          </label>
                          <input
                            type="url"
                            value={((editedUser as any).streamingLinks || {})[platform] || ''}
                            onChange={(e) =>
                              setEditedUser((p: any) => ({
                                ...p,
                                streamingLinks: { ...(p.streamingLinks || {}), [platform]: e.target.value },
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder={`https://${platform}.com/yourprofile`}
                          />
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* IMAGES TAB */}
            {activeTab === 'images' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Images</h3>
                <div className="space-y-6">
                  {/* Avatar */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Profile Avatar</label>
                    <div className="flex items-center space-x-4">
                      <img
                        src={avatarPreview || (editedUser as any).avatar}
                        alt="Avatar preview"
                        className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                      />
                      <div className="flex-1">
                        <div className="space-y-2">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleImageUpload('avatar', f);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          />
                          <p className="text-xs text-gray-500">Upload an image file (JPG, PNG, etc.)</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cover */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cover Image</label>
                    <div className="space-y-3">
                      <img
                        src={coverPreview || (editedUser as any).coverImage || DEFAULT_COVER}
                        alt=""
                        onError={(e) => {
                          const img = e.currentTarget as HTMLImageElement;
                          if (img.src !== REMOTE_FALLBACK_COVER_URL) img.src = REMOTE_FALLBACK_COVER_URL;
                        }}
                        className="w-full h-24 rounded-lg object-cover border-2 border-gray-200"
                      />
                      <div className="space-y-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleImageUpload('cover', f);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <p className="text-xs text-gray-500">Upload an image file (JPG, PNG, etc.)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SUBSCRIPTION TAB */}
            {activeTab === 'subscription' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Subscription Management</h3>
                <p className="text-sm text-gray-600">
                  Assign, upgrade, or downgrade the user's plan and set renewal period.
                </p>

                {/* For simplicity, we reuse user's existing sub fields; if you have a dedicated control, wire it here */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                    <select
                      value={(editedUser.subscription?.type as any) || 'free'}
                      onChange={(e) =>
                        setEditedUser((p: any) => ({
                          ...p,
                          subscription: { ...(p.subscription || {}), type: e.target.value },
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="free">Free</option>
                      <option value="trial">Trial (1 month)</option>
                      <option value="silver">Silver</option>
                      <option value="gold">Gold</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
                    <input
                      type="datetime-local"
                      value={(() => {
                        const d = editedUser.subscription?.expiresAt as any;
                        if (!d) return '';
                        const iso = new Date(d).toISOString();
                        return iso.slice(0, 16);
                      })()}
                      onChange={(e) =>
                        setEditedUser((p: any) => ({
                          ...p,
                          subscription: {
                            ...(p.subscription || {}),
                            expiresAt: e.target.value ? new Date(e.target.value).toISOString() : undefined,
                          },
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  Current: {(editedUser.subscription?.type || 'free').toUpperCase()}{' '}
                  {editedUser.subscription?.expiresAt
                    ? `• Expires ${new Date(editedUser.subscription.expiresAt as any).toLocaleDateString()}`
                    : ''}
                </div>
              </div>
            )}

            {/* PASSWORD TAB (Admin only) */}
            {activeTab === 'password' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Admin: Set User Password</h3>
                <p className="text-xs text-gray-600">Set a new password for this user. This does not reveal the current password.</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">New Password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                        permLoading || !canSetPassword ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                      disabled={permLoading || !canSetPassword}
                      placeholder="At least 8 characters"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Confirm Password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                        permLoading || !canSetPassword ? 'bg-gray-50 cursor-not-allowed' : ''
                      }`}
                      disabled={permLoading || !canSetPassword}
                      placeholder="Re-type password"
                    />
                  </div>
                </div>

                {(() => {
                  const c = pwdChecks(newPassword);
                  const pct = Math.min(100, Math.max(0, (c.score / 4) * 100));
                  return (
                    <div className="mt-1">
                      <div className="h-2 w-full bg-gray-200 rounded">
                        <div className={`h-2 rounded ${c.color}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="mt-1 text-xs text-gray-700">
                        Strength: <span className="font-semibold">{c.label}</span>
                      </div>
                      <ul className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                        <li className={c.lengthOk ? 'text-green-600' : 'text-gray-600'}>• At least 8 characters</li>
                        <li className={c.hasNumber ? 'text-green-600' : 'text-gray-600'}>• Contains a number</li>
                        <li className={c.hasSymbol ? 'text-green-600' : 'text-gray-600'}>• Contains a symbol</li>
                      </ul>
                    </div>
                  );
                })()}

                <div className="mt-3 flex items-center justify-between">
                  <label className="flex items-center text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={showPassword}
                      onChange={(e) => setShowPassword(e.target.checked)}
                      disabled={permLoading || !canSetPassword}
                    />
                    Show passwords
                  </label>
                  <button
                    type="button"
                    onClick={handleAdminSetPassword}
                    disabled={
                      permLoading ||
                      !canSetPassword ||
                      (() => {
                        const c = pwdChecks(newPassword);
                        return isSettingPassword || !c.lengthOk || !c.hasNumber || !c.hasSymbol || newPassword !== confirmPassword;
                      })()
                    }
                    className={`px-4 py-2 rounded-lg text-white transition-colors ${
                      permLoading ||
                      !canSetPassword ||
                      (() => {
                        const c = pwdChecks(newPassword);
                        return isSettingPassword || !c.lengthOk || !c.hasNumber || !c.hasSymbol || newPassword !== confirmPassword;
                      })()
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isSettingPassword ? 'Setting…' : 'Set Password'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Live Preview (auto-resizing height) */}
          <div className="w-1/2 p-6 bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col overflow-y-auto">
            <div className="flex items-center mb-4">
              <Eye className="w-5 h-5 mr-2 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Live Preview</h3>
            </div>
            {/* Auto-size wrapper: fixed width bezel, height grows with content */}
            <div className="flex-1 flex items-start justify-center">
              <div style={{ width: FIXED_PHONE_WIDTH }} className="">
                <PhoneBezel>
                  <div className="w-full overflow-visible">
                    {renderTemplatePreview()}
                  </div>
                </PhoneBezel>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

